import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import {
  CustomButton,
  InfoMessage,
} from "components/CustomComponents/CustomButton";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  drawerStyle,
} from "services/constants";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Drawer,
  IconButton,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { FilterProvider } from "pages/FilterContext";

import AdvanceSalaryPanel, {
  buildStatusHistory,
  CustomTimeline,
} from "./AdvanceSalaryFormPanel";
import {
  DateRange,
  Comment,
  ModeStandby,
  CurrencyExchange,
} from "@mui/icons-material";

// 1. Define the headers to match your screenshot exactly
const advanceSalaryHeader = [
  {
    key: "request_date",
    name: "Date",
    align: "left",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "ot_date",
      toField: "ot_date",
    },
  },
  {
    key: "effective_amount",
    name: "Amount",
    align: "left",
    type: "number",
    icon: <CurrencyExchange fontSize="small" />,
  },
  {
    key: "repayment_mode",
    name: "Repayment Mode",
    align: "left",
    type: "string",
    icon: <Comment fontSize="small" />,
  },
  {
    key: "effective_repayment_date",
    name: "Repayment Date",
    align: "left",
    type: "date",
    icon: <DateRange fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "ot_date",
      toField: "ot_date",
    },
  },
  {
    key: "status",
    name: "Status",
    align: "left",
    type: "string",
    icon: <ModeStandby fontSize="small" />,
  },
];

const listHeader = [
  { key: "expand", name: "", align: "center" },
  ...advanceSalaryHeader,
];

const AdvanceSalaryContent = () => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredAdvances, setFilteredAdvances] = useState([]);

  // Timeline Expansion State
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);

  // Drawer & Mode State
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("view"); // "create", "view", or "edit"
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  const containerRef = useRef(null);

  const {
    result: advanceData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}advance/request/?selected_user_id=${userDetails?.id}`);

  // 2. Safely extract the data array from the API response
  useEffect(() => {
    const records = advanceData?.data || [];
    setFilteredAdvances(records);
  }, [advanceData]);

  useEffect(() => {
    if (userDetails?.id) reCallApi(true);
  }, [reCallApi, userDetails]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setFilteredAdvances(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleToggle = (e, index, value) => {
    e.stopPropagation(); // Prevent the row click event from firing
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  // 3. Handlers for different modes
  const handleCreate = () => {
    setPanelMode("create");
    setSelectedAdvance(null);
    setIsPanelOpen(true);
  };

  const handleView = (row) => {
    setPanelMode("view");
    setSelectedAdvance(row);
    setIsPanelOpen(true);
  };

  const handleEdit = () => {
    setPanelMode("edit"); // Keeps the drawer open, just switches the UI mode
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedAdvance(null), 300); // Clear data after drawer closes
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Advance Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View all pending and historical advance salary requests.
        </Typography>
      </Box>
      <Paper sx={{ width: "100%", height: "78vh" }}>
        <CustomFilter
          title={"Advance Salary Records"}
          filterHeaders={listHeader}
          data={advanceData?.data || []} // Pass the extracted array
          filteredData={filteredAdvances}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess={true}
          newButtonComponent={
            <CustomButton
              actionFuntion={handleCreate}
              actionName="New"
              typeName="button"
            />
          }
        />

        <Grid sx={{ ml: 2, mr: 2 }}>
          <TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
            <Table>
              {/* Header Styled to match screenshot */}
              <TableHead sx={{ ...stickyHeader, bgcolor: "#f5f5f5" }}>
                <TableRow sx={tableRowSize}>
                  {listHeader?.map((value) => (
                    <TableCell
                      key={value.key}
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        borderBottom: "1px solid #ddd",
                        bgcolor: "#f5f5f5", // Light grey header from design
                      }}
                      align={value.align || "center"}
                    >
                      {value.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdvances?.length === 0 ? (
                  <InfoMessage dataLength={advanceData?.data || []} />
                ) : (
                  filteredAdvances
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                    ?.map((row, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            ...tableRowSize,
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#fafafa" },
                          }}
                          onClick={() => handleView(row)} // Click anywhere on row to view
                        >
                          {listHeader.map((head) => (
                            <TableCell
                              key={head.key}
                              align={head.align || "center"}
                              sx={{ borderBottom: "1px solid #f0f0f0" }}
                            >
                              {head.key === "expand" ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggle(e, index, row)}
                                >
                                  <ArrowRightIcon
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      color:
                                        expandedIndex === index
                                          ? "#00ab66"
                                          : "inherit",
                                    }}
                                  />
                                </IconButton>
                              ) : head.key === "status" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <StatusBadge
                                    color={row?.color_code}
                                    label={row?.status || "N/A"}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2">
                                  {row[head.key] ?? "-"}
                                </Typography>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>

                        {/* Expandable Timeline Row */}
                        {expandedIndex === index && (
                          <TableRow>
                            <TableCell
                              colSpan={listHeader.length}
                              sx={{ p: 0, borderBottom: "1px solid #eee" }}
                            >
                              <Box sx={{ pl: 6, py: 2, bgcolor: "#fafafa" }}>
                                <CustomTimeline
                                  history={buildStatusHistory(expandedData)}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1 }}>
            {filteredAdvances?.length > 25 && (
              <TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[25, 50, 75, 100]}
                count={filteredAdvances?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Box>
        </Grid>
      </Paper>

      {/* Unified Side Drawer */}
      <Drawer
        anchor="right"
        open={isPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handlePanelClose();
        }}
        sx={drawerStyle}
      >
        {isPanelOpen && (
          <AdvanceSalaryPanel
            mode={panelMode} // Passes 'create', 'view', or 'edit'
            initialData={selectedAdvance} // The clicked row's data
            onClose={handlePanelClose}
            onEdit={handleEdit} // Passed to View mode so it can trigger Edit
            reCallApi={reCallApi}
          />
        )}
      </Drawer>
    </Box>
  );
};

export const AdvanceSalary = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );

  const advModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Apply",
  );
  const subModuleId = advModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Advance Salary",
  )?.sub_module_id;

  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <AdvanceSalaryContent />
    </FilterProvider>
  );
};
